import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, ExternalLink } from 'lucide-react';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import { Button, ConfirmDialog, Spinner } from '@/components/ui';
import {
  useInfiniteNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteReadNotifications,
} from '@/features/notifications/hooks/useNotifications';
import { resolveNotificationInternalPath } from '@/features/notifications/utils/notificationNavigation';
import {
  extractShiftAlertContext,
  getNotificationActionStatus,
  isConfirmShiftActionAvailable,
  isReadOnlyShiftAction,
  isShiftAlertNotification,
} from '@/features/notifications/utils/shiftAlertNotificationContext';
import { useConfirmShiftAssignment } from '@/features/shifts/hooks/useShifts';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/cn';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { ChatNotificationContent } from '@/features/notifications/components/ChatNotificationContent';
import { getChatNotificationContext } from '@/features/notifications/utils/chatNotificationContext';

const SHIFT_STATUS_LABEL: Record<string, string> = {
  expired: 'Expired',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
};

function formatTime(iso: string): string {
  try {
    const date = parseISO(iso);
    const now = new Date();
    const diffDays = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays < 1) return formatDistanceToNow(date, { addSuffix: true });
    return format(date, 'PPp');
  } catch {
    return iso;
  }
}

export function NotificationDropdown(): React.ReactElement {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [showClearReadConfirm, setShowClearReadConfirm] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const {
    data: listData,
    isLoading: listLoading,
    error: listError,
    refetch: refetchNotifications,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteNotifications({
    limit: 10,
  });
  const { data: unreadCount = 0 } = useUnreadCount();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteRead = useDeleteReadNotifications();
  const confirmShift = useConfirmShiftAssignment();

  const notifications = useMemo(
    () => listData?.pages.flatMap((page) => page.data) ?? [],
    [listData]
  );
  const notificationTotal = listData?.pages[0]?.total ?? notifications.length;
  const hasReadNotifications = notificationTotal > unreadCount;

  // Collapse repeated shift alerts (same title) into one expandable group so a
  // burst of "Shift availability not confirmed" doesn't bury other notifications.
  // Non-shift notifications, and singleton shift alerts, render standalone.
  const notificationGroups = useMemo(() => {
    const groups: { key: string; items: typeof notifications }[] = [];
    const indexByKey = new Map<string, number>();
    notifications.forEach((n) => {
      const key = isShiftAlertNotification(n) ? `shift:${n.title}` : `solo:${n._id}`;
      const at = indexByKey.get(key);
      if (at === undefined) {
        indexByKey.set(key, groups.length);
        groups.push({ key, items: [n] });
      } else {
        groups[at].items.push(n);
      }
    });
    return groups;
  }, [notifications]);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleMarkAllRead = useCallback(() => {
    if (unreadCount === 0) return;
    markAllAsRead.mutate({});
  }, [markAllAsRead, unreadCount]);

  const handleClearReadClick = useCallback(() => {
    setShowClearReadConfirm(true);
  }, []);

  const handleConfirmClearRead = useCallback(() => {
    deleteRead.mutate(undefined);
    setShowClearReadConfirm(false);
  }, [deleteRead]);

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleOpenNotification = useCallback(
    (n: (typeof notifications)[number]) => {
      const internalPath = resolveNotificationInternalPath(n, { can });
      const externalUrl = !internalPath ? n.reference?.url : undefined;
      if (!internalPath && !externalUrl) return;
      setIsOpen(false);
      if (!n.isRead) markAsRead.mutate({ id: n._id, silent: true });
      if (internalPath) {
        navigate(internalPath);
      } else if (externalUrl) {
        window.open(externalUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [can, markAsRead, navigate]
  );

  // Confirms the shift inline from the dropdown CTA. The mutation already
  // invalidates assignment views + notifications and toasts on success; we
  // additionally mark the notification read so its unread styling clears.
  const handleConfirmShift = useCallback(
    (n: (typeof notifications)[number]) => {
      const { companyId, shiftAssignmentId } = extractShiftAlertContext(n);
      if (!companyId || !shiftAssignmentId) {
        // Fall back to opening the shift so the user can confirm there.
        handleOpenNotification(n);
        return;
      }
      confirmShift.mutate(
        { companyId, id: shiftAssignmentId },
        {
          onSuccess: () => {
            if (!n.isRead) markAsRead.mutate({ id: n._id, silent: true });
          },
        }
      );
    },
    [confirmShift, handleOpenNotification, markAsRead]
  );

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    const handleClickOutside = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    const root = scrollContainerRef.current;
    if (!isOpen || !sentinel || !root || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      {
        root,
        rootMargin: '160px 0px',
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isOpen, notifications.length]);

  const renderRow = (n: (typeof notifications)[number]): React.ReactElement => {
    const internalPath = resolveNotificationInternalPath(n, { can });
    const externalUrl = !internalPath ? n.reference?.url : undefined;
    const hasTarget = Boolean(internalPath || externalUrl);
    const isShiftAlert = isShiftAlertNotification(n);
    const shiftReadOnly = isShiftAlert && isReadOnlyShiftAction(n);
    const shiftStatusLabel = shiftReadOnly
      ? SHIFT_STATUS_LABEL[getNotificationActionStatus(n) ?? '']
      : undefined;
    // Managers can't confirm on an employee's behalf — only the owner-confirm
    // notification gets a "Confirm" label; everyone else reviews the assignment
    // (Edit/Cancel/Reassign live there).
    const canConfirmShift = isConfirmShiftActionAvailable(n);
    const shiftCtaLabel = canConfirmShift ? 'Confirm shift' : 'Review shift';
    const showShiftCta = isShiftAlert && !shiftReadOnly && hasTarget;
    const confirmPending =
      confirmShift.isPending &&
      confirmShift.variables?.id === extractShiftAlertContext(n).shiftAssignmentId;
    const isChatNotification = Boolean(getChatNotificationContext(n));
    return (
      <li
        key={n._id}
        className={cn(
          'rounded-xl border px-3 py-2.5 transition-colors',
          n.isRead
            ? 'border-gray-100 bg-white hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800/60'
            : 'border-primary/20 bg-primary/5 hover:bg-primary/10 dark:border-primary/30 dark:bg-primary/10'
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={() => handleOpenNotification(n)}
            disabled={!hasTarget}
            aria-label={hasTarget ? `Open ${n.title}` : undefined}
            className={cn(
              'min-w-0 flex-1 text-left',
              hasTarget
                ? 'cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
                : 'cursor-default'
            )}
          >
            {isChatNotification ? (
              <ChatNotificationContent
                notification={n}
                timestamp={n.createdAt ? formatTime(n.createdAt) : undefined}
              />
            ) : (
              <>
                <span className="flex items-center gap-1">
                  <span
                    className={cn(
                      'truncate text-sm',
                      n.isRead
                        ? 'text-gray-700 dark:text-gray-300'
                        : 'font-semibold text-gray-950 dark:text-gray-100'
                    )}
                  >
                    {n.title}
                  </span>
                  {externalUrl && (
                    <ExternalLink
                      className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500"
                      aria-hidden
                    />
                  )}
                </span>
                {n.message && (
                  <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
                    {n.message}
                  </span>
                )}
                <span className="mt-1 block text-[11px] text-gray-400 dark:text-gray-500">
                  {n.createdAt ? formatTime(n.createdAt) : ''}
                </span>
              </>
            )}
          </button>
          {!n.isRead && (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                className="text-xs"
                onClick={() => markAsRead.mutate({ id: n._id })}
                disabled={markAsRead.isPending && markAsRead.variables?.id === n._id}
              >
                Mark read
              </Button>
            </div>
          )}
        </div>
        {(showShiftCta || shiftStatusLabel) && (
          <div className="mt-2 flex items-center gap-2 pl-0.5">
            {showShiftCta ? (
              <Button
                type="button"
                variant="primary"
                className="text-xs"
                loading={confirmPending}
                onClick={() =>
                  canConfirmShift ? handleConfirmShift(n) : handleOpenNotification(n)
                }
              >
                {shiftCtaLabel}
              </Button>
            ) : (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                {shiftStatusLabel}
              </span>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        className="relative rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100/80 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-white/5 dark:hover:text-gray-300"
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-white"
            aria-label={`${unreadCount} unread notifications`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-[min(360px,92vw)] overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.14)] backdrop-blur dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/30"
          role="menu"
          aria-label="Notification list"
        >
          <div className="border-b border-gray-100 px-3.5 py-3 dark:border-gray-800">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  <span
                    className={cn(
                      'inline-flex h-2 w-2 rounded-full',
                      unreadCount > 0 ? 'animate-pulse bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                    )}
                    aria-hidden
                  />
                  Notifications
                </div>
                <p className="mt-1 truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
                  {unreadCount > 0
                    ? `${unreadCount} unread`
                    : notifications.length > 0
                      ? 'All caught up'
                      : 'No notifications'}
                </p>
              </div>
              {unreadCount > 0 && (
                <span className="shrink-0 rounded-full bg-gray-900 px-2.5 py-1 text-xs font-semibold text-white dark:bg-gray-100 dark:text-gray-900">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            className="thin-scroll max-h-[min(340px,64vh)] space-y-1.5 overflow-y-auto p-2"
          >
            {listLoading && (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            )}
            {listError && (
              <QueryErrorState
                error={listError}
                size="inline"
                title="Couldn't load notifications"
                onRetry={refetchNotifications}
              />
            )}
            {!listLoading && !listError && notifications.length === 0 && (
              <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-sm dark:border-gray-800 dark:bg-gray-800/55">
                <p className="font-medium text-gray-800 dark:text-gray-100">No notifications</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  You'll see new alerts and reminders here.
                </p>
              </div>
            )}
            {!listLoading && !listError && notifications.length > 0 && (
              <ul className="space-y-1.5">
                {notificationGroups.map((group) => {
                  if (group.items.length === 1) return renderRow(group.items[0]);
                  const expanded = expandedGroups.has(group.key);
                  const head = group.items[0];
                  const groupUnread = group.items.filter((n) => !n.isRead).length;
                  return (
                    <li key={group.key}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.key)}
                        aria-expanded={expanded}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-colors',
                          groupUnread > 0
                            ? 'border-primary/20 bg-primary/5 hover:bg-primary/10 dark:border-primary/30 dark:bg-primary/10'
                            : 'border-gray-100 bg-white hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800/60'
                        )}
                      >
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 shrink-0 text-gray-400 transition-transform dark:text-gray-500',
                            expanded ? '' : '-rotate-90'
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span
                            className={cn(
                              'block truncate text-sm',
                              groupUnread > 0
                                ? 'font-semibold text-gray-950 dark:text-gray-100'
                                : 'text-gray-700 dark:text-gray-300'
                            )}
                          >
                            {head.title}
                          </span>
                          <span className="mt-0.5 block text-[11px] text-gray-400 dark:text-gray-500">
                            {group.items.length} alerts
                            {groupUnread > 0 ? ` · ${groupUnread} unread` : ''}
                          </span>
                        </span>
                        <span className="shrink-0 rounded-full bg-gray-900 px-2 py-0.5 text-[11px] font-semibold text-white dark:bg-gray-100 dark:text-gray-900">
                          {group.items.length}
                        </span>
                      </button>
                      {expanded && (
                        <ul className="mt-1.5 space-y-1.5 pl-3">
                          {group.items.map((n) => renderRow(n))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {!listLoading && !listError && hasNextPage && (
              <div
                ref={loadMoreRef}
                className="flex items-center justify-center py-2 text-xs text-gray-500 dark:text-gray-400"
              >
                {isFetchingNextPage ? (
                  <span className="inline-flex items-center gap-2">
                    <Spinner size="sm" />
                    Loading more...
                  </span>
                ) : (
                  <span>Scroll to load more</span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3.5 py-2.5 dark:border-gray-800">
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-xs"
                  onClick={handleMarkAllRead}
                  loading={markAllAsRead.isPending}
                >
                  Mark all read
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                className="text-xs"
                onClick={handleClearReadClick}
                disabled={!hasReadNotifications}
                loading={deleteRead.isPending}
              >
                Clear read
              </Button>
            </div>
            <Link
              to="/notifications"
              className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-primary transition-colors hover:text-primary/80"
              onClick={() => setIsOpen(false)}
            >
              View all
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showClearReadConfirm}
        onClose={() => setShowClearReadConfirm(false)}
        onConfirm={handleConfirmClearRead}
        title="Delete read notifications"
        message="Delete all read notifications? This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteRead.isPending}
      />
    </div>
  );
}

NotificationDropdown.displayName = 'NotificationDropdown';
